// @info The RiskTable component is a table display for project risks. It receives columns, risks data, and risk totals data as props. It uses the useState hook to manage state for risks, risk totals, and a loading state. It also provides a function handleSaveCell to update a risk and refresh the table. The updated risks and totals are fetched from a service updateRisk.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import updateRisk from "@/features/risk/api/editRiskService";
import getProjectBaseRisks from "@/features/risk/api/riskBaselineService";
import AddRisk from "@/features/risk/components/AddRisk";
import TableForRisk from "@/features/risk/components/TableForRisk";
import { RiskBaseModel } from "@/features/risk/types/riskModels";
import { OverviewItemsModel } from "@/shared/components/baselineOverview/model/baselineOverviewModels";
import useProjectWBSContext from "@/shared/context/projectWBS/useProjectWBSContext";

type RiskTableProps = {
  risksData: RiskBaseModel[];
  overviewItems: OverviewItemsModel[];
};

export default function RiskTable({ risksData, overviewItems }: RiskTableProps) {
  const [risks, setRisks] = useState<RiskBaseModel[]>([]);
  const { outcome: wbsEntryId } = useParams();

  const { refetchProjectWBS } = useProjectWBSContext();

  useEffect(() => {
    setRisks(risksData);
  }, [risksData]);

  const handleSaveCell = async (
    riskToUpdate: RiskBaseModel,
    name: string,
    value: number | string
  ) => {
    const updatedRisk = {
      ...riskToUpdate,
      [name]: value,
    };
    try {
      const RiskBaseline = await updateRisk(updatedRisk);
      setRisks(RiskBaseline);
      refetchProjectWBS();
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to save cell: ${error.message}`);
      } else {
        throw new Error("An unknown error occurred while saving cell");
      }
    }
  };

  const handleRefreshRisk = async () => {
    try {
      if (!wbsEntryId) {
        throw new Error("Outcome is null");
      }
      const updatedRisks = await getProjectBaseRisks(wbsEntryId);
      refetchProjectWBS();
      setRisks(updatedRisks);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to refresh risks: ${error.message}`);
      } else {
        throw new Error("An unknown error occurred while refreshing risks");
      }
    }
  };

  return (
    <>
      <AddRisk refetchRisk={() => void handleRefreshRisk()} />
      <TableForRisk
        risks={risks}
        handleSaveCell={(riskToUpdate, columnId, newValue) =>
          void handleSaveCell(riskToUpdate, columnId, newValue)
        }
        refetchRisk={() => void handleRefreshRisk()}
        overviewItems={overviewItems}
      />
    </>
  );
}
